/*
  pdp-lower.js — shared runtime for the sections below the product buy box
  (image-slider, image-with-text, testimonials). Owned by the pdp-lower agent.

  Defines two custom elements the source theme ships in main.js / secondary.js and
  that global.js does NOT define:
    <internal-video>   – lazy self-hosted video (HLS source dropped, MP4 data-src swapped in
                         when the element scrolls into view, play/sound buttons, spinner).
    <splide-component> – a native re-implementation of the Shrine Splide wrapper. It reads the
                         same data-* options the theme's SplideComponent reads and produces the
                         same DOM (arrow buttons inside .splide__arrows, <li><button> dots inside
                         .splide__pagination, is-active / is-visible / is-overflow classes) so
                         base.css styles it identically.
  Both definitions are guarded so loading this file more than once, or alongside another
  section that defines the same element, is harmless.
*/
(function () {
  'use strict';

  /* ---------------------------------------------------------------- internal-video */
  if (!customElements.get('internal-video')) {
    class InternalVideo extends HTMLElement {
      constructor() {
        super();
        this.playButton = this.querySelector('.internal-video__play');
        this.noPlayBtn = this.dataset.noPlayBtn === 'true';
        this.loaded = false;
        this.soundButton = this.querySelector('.internal-video__sound-btn');
        this.video = this.querySelector('video');
        if (!this.video) return;
        // The source theme always removes the HLS source and lets the browser use the MP4.
        var hls = this.video.querySelector('source[type="application/x-mpegURL"]');
        if (hls) hls.remove();
        if (this.playButton) this.playButton.addEventListener('click', this.playVideo.bind(this));
        if (this.soundButton) this.soundButton.addEventListener('click', this.toggleSound.bind(this));
        this.video.addEventListener('ended', this.endedVideo.bind(this));
        this.video.addEventListener('waiting', this.showSpinner.bind(this));
        this.video.addEventListener('canplaythrough', this.hideSpinner.bind(this));
        this.video.addEventListener('play', this.hideSpinner.bind(this));
        if (this.dataset.autoplay === 'true' && 'IntersectionObserver' in window) {
          this.observer = new IntersectionObserver(this.handleIntersection.bind(this), {
            root: null,
            rootMargin: '0px',
            threshold: 0.05
          });
          this.observer.observe(this);
        }
      }

      loadSources() {
        var sources = this.video.querySelectorAll('source[data-src]');
        for (var i = 0; i < sources.length; i++) {
          sources[i].setAttribute('src', sources[i].getAttribute('data-src'));
          sources[i].removeAttribute('data-src');
        }
        this.loaded = true;
      }

      playVideo() {
        var self = this;
        if (this.video.paused) {
          if (!this.loaded) {
            this.loadSources();
            this.video.load();
          }
          this.classList.add('internal-video--playing');
          if (this.playButton && this.noPlayBtn) this.playButton.style.visibility = 'hidden';
          var p = this.video.play();
          if (p !== undefined) {
            p.then(function () {
              self.classList.add('internal-video--playing');
              if (self.playButton && self.noPlayBtn) self.playButton.style.visibility = 'hidden';
            }).catch(function () {
              self.classList.remove('internal-video--playing');
              if (self.playButton && self.noPlayBtn) self.playButton.style.visibility = 'visible';
            });
          }
        } else {
          this.video.pause();
          this.classList.remove('internal-video--playing');
        }
      }

      endedVideo() {
        this.classList.remove('internal-video--playing');
      }

      toggleSound() {
        if (this.video.muted) {
          this.video.muted = false;
          this.classList.remove('internal-video--muted');
        } else {
          this.video.muted = true;
          this.classList.add('internal-video--muted');
        }
      }

      showSpinner() {
        this.classList.add('internal-video--loading');
      }

      hideSpinner() {
        this.classList.remove('internal-video--loading');
      }

      handleIntersection(entries) {
        var self = this;
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          self.loadSources();
          self.video.load();
          var p = self.video.play();
          if (p !== undefined) {
            p.then(function () {
              self.classList.add('internal-video--playing');
            }).catch(function (err) {
              if (err && err.name === 'NotAllowedError') {
                self.classList.remove('internal-video--playing');
                if (self.playButton && self.noPlayBtn) self.playButton.style.visibility = 'visible';
              }
            });
          }
          self.observer.disconnect();
        });
      }
    }
    customElements.define('internal-video', InternalVideo);
  }

  /* ---------------------------------------------------------------- splide-component */
  if (!customElements.get('splide-component')) {
    var ARROW_PATH = 'm15.5 0.932-4.3 4.38 14.5 14.6-14.5 14.5 4.3 4.4 14.6-14.6 4.4-4.3-4.4-4.4-14.6-14.6z';
    var MOBILE_MQ = '(max-width: 749px)';
    var EASING = 'cubic-bezier(0.25, 1, 0.5, 1)';
    var SPEED = 400;

    function toInt(value, fallback) {
      var n = parseInt(value, 10);
      return isNaN(n) ? fallback : n;
    }

    class SplideComponent extends HTMLElement {
      constructor() {
        super();
        this.root = this.querySelector('.splide');
        this.onResize = this.onResize.bind(this);
        this.onPointerDown = this.onPointerDown.bind(this);
        this.onPointerMove = this.onPointerMove.bind(this);
        this.onPointerUp = this.onPointerUp.bind(this);
        this.onClickCapture = this.onClickCapture.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
      }

      connectedCallback() {
        if (!this.root || this.mounted !== undefined) return;
        this.track = this.root.querySelector('.splide__track');
        this.list = this.root.querySelector('.splide__list');
        this.slides = this.list ? Array.prototype.slice.call(this.list.children).filter(function (el) {
          return el.classList.contains('splide__slide');
        }) : [];
        if (!this.track || !this.list || !this.slides.length) return;

        var d = this.dataset;
        this.type = d.type || 'slide';
        this.rewind = this.type === 'fade' || d.rewind === 'true';
        this.arrowsEnabled = d.arrows !== 'false';
        this.paginationEnabled = d.pagination !== 'false';
        this.omitEnd = d.omitEnd !== 'false';
        this.arrowClass = 'splide__arrow' + (d.arrowsColor ? ' color-' + d.arrowsColor : '');
        this.pageClass = 'splide__pagination__page' + (d.dotsColor ? ' color-' + d.dotsColor + ' dots-custom-color' : '');
        this.pauseVideos = d.pauseVideos === 'true';
        this.opts = {
          desktop: {
            perPage: toInt(d.slidesDesktop, 3),
            perMove: toInt(d.perMoveDesktop, 1),
            gap: d.gapDesktop !== undefined ? toInt(d.gapDesktop, 30) : 30,
            padding: toInt(d.sidePaddingDesktop, 0),
            destroy: d.destroyDesktop === 'true'
          },
          mobile: {
            perPage: toInt(d.slidesMobile, 1),
            perMove: toInt(d.perMoveMobile, 1),
            gap: d.gapMobile !== undefined ? toInt(d.gapMobile, 15) : 15,
            padding: toInt(d.sidePaddingMobile, 0),
            destroy: d.destroyMobile === 'true'
          }
        };
        this.index = 0;
        this.mounted = false;
        this.mq = window.matchMedia(MOBILE_MQ);
        var self = this;
        var onChange = function () { self.refresh(); };
        if (this.mq.addEventListener) this.mq.addEventListener('change', onChange);
        else this.mq.addListener(onChange);
        window.addEventListener('resize', this.onResize);
        this.refresh();
      }

      disconnectedCallback() {
        window.removeEventListener('resize', this.onResize);
      }

      get current() {
        return this.mq.matches ? this.opts.mobile : this.opts.desktop;
      }

      refresh() {
        if (this.current.destroy) {
          if (this.mounted) this.destroy();
          this.root.classList.add('is-initialized');
          return;
        }
        if (!this.mounted) this.mount();
        this.layout();
        this.go(Math.min(this.index, this.end), true);
      }

      /* ---- mount / destroy */
      mount() {
        var self = this;
        this.mounted = true;
        this.root.classList.add('splide--ltr', 'is-initialized', 'is-active');
        this.track.classList.add('splide__track--draggable');
        this.track.style.touchAction = 'pan-y';
        this.list.style.willChange = 'transform';
        this.slides.forEach(function (slide, i) {
          slide.setAttribute('role', 'group');
          slide.setAttribute('aria-roledescription', 'slide');
          slide.setAttribute('aria-label', (i + 1) + ' of ' + self.slides.length);
        });

        var arrowsWrap = this.root.querySelector('.splide__arrows');
        if (this.arrowsEnabled && arrowsWrap) {
          arrowsWrap.classList.add('splide__arrows--ltr');
          this.prevBtn = this.createArrow('prev', 'Previous slide');
          this.nextBtn = this.createArrow('next', 'Next slide');
          arrowsWrap.appendChild(this.prevBtn);
          arrowsWrap.appendChild(this.nextBtn);
          this.prevBtn.addEventListener('click', function () { self.go(self.index - self.current.perMove); });
          this.nextBtn.addEventListener('click', function () { self.go(self.index + self.current.perMove); });
        }

        this.pagination = this.root.querySelector('.splide__pagination');
        if (this.paginationEnabled && this.pagination) {
          this.pagination.classList.add('splide__pagination--ltr');
          this.pagination.setAttribute('role', 'tablist');
          this.pagination.setAttribute('aria-label', 'Select a slide to show');
        }

        this.track.addEventListener('pointerdown', this.onPointerDown);
        this.track.addEventListener('click', this.onClickCapture, true);
        this.root.addEventListener('keydown', this.onKeydown);
      }

      destroy() {
        var self = this;
        this.mounted = false;
        this.root.classList.remove('splide--ltr', 'is-active', 'is-overflow');
        this.track.classList.remove('splide__track--draggable');
        this.track.style.touchAction = '';
        this.track.style.paddingLeft = '';
        this.track.style.paddingRight = '';
        this.list.style.transform = '';
        this.list.style.transition = '';
        this.list.style.willChange = '';
        this.slides.forEach(function (slide) {
          slide.style.width = '';
          slide.style.marginRight = '';
          slide.classList.remove('is-active', 'is-visible', 'is-prev', 'is-next', 'is-instant-active');
          slide.removeAttribute('aria-hidden');
        });
        if (this.prevBtn) { this.prevBtn.remove(); this.prevBtn = null; }
        if (this.nextBtn) { this.nextBtn.remove(); this.nextBtn = null; }
        var arrowsWrap = this.root.querySelector('.splide__arrows');
        if (arrowsWrap) arrowsWrap.classList.remove('splide__arrows--ltr');
        if (this.pagination) {
          this.pagination.innerHTML = '';
          this.pagination.classList.remove('splide__pagination--ltr');
        }
        this.track.removeEventListener('pointerdown', this.onPointerDown);
        this.track.removeEventListener('click', this.onClickCapture, true);
        this.root.removeEventListener('keydown', this.onKeydown);
        self.style.removeProperty('--active-slide-height');
      }

      createArrow(dir, label) {
        var btn = document.createElement('button');
        btn.className = this.arrowClass + ' splide__arrow--' + dir;
        btn.type = 'button';
        btn.setAttribute('aria-label', label);
        btn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40" width="40" height="40" focusable="false"><path d="' + ARROW_PATH + '"></path></svg>';
        return btn;
      }

      /* ---- layout */
      layout() {
        var o = this.current;
        var n = this.slides.length;
        this.track.style.paddingLeft = o.padding + 'px';
        this.track.style.paddingRight = o.padding + 'px';
        this.slides.forEach(function (slide) {
          slide.style.width = 'calc((100% + ' + o.gap + 'px) / ' + o.perPage + ' - ' + o.gap + 'px)';
          slide.style.marginRight = o.gap + 'px';
        });
        this.end = this.omitEnd ? Math.max(0, n - o.perPage) : n - 1;
        this.overflow = n > o.perPage;
        this.root.classList.toggle('is-overflow', this.overflow);
        this.pages = this.omitEnd ? Math.ceil(Math.max(0, n - o.perPage) / o.perMove) + 1 : Math.ceil(n / o.perMove);
        this.buildPagination();
      }

      buildPagination() {
        var self = this;
        if (!this.paginationEnabled || !this.pagination) return;
        this.pagination.innerHTML = '';
        this.pageButtons = [];
        for (var i = 0; i < this.pages; i++) {
          var li = document.createElement('li');
          li.setAttribute('role', 'presentation');
          var btn = document.createElement('button');
          btn.className = this.pageClass;
          btn.type = 'button';
          btn.setAttribute('role', 'tab');
          btn.setAttribute('aria-label', 'Go to slide ' + (i + 1));
          (function (page) {
            btn.addEventListener('click', function () { self.go(self.pageToIndex(page)); });
          })(i);
          li.appendChild(btn);
          this.pagination.appendChild(li);
          this.pageButtons.push(btn);
        }
      }

      pageToIndex(page) {
        return Math.min(page * this.current.perMove, this.end);
      }

      indexToPage(index) {
        return index >= this.end ? this.pages - 1 : Math.floor(index / this.current.perMove);
      }

      /* ---- movement */
      clamp(index) {
        if (this.rewind) {
          if (index < 0) return this.end;
          if (index > this.end) return 0;
          return index;
        }
        return Math.max(0, Math.min(index, this.end));
      }

      positionOf(index) {
        var slide = this.slides[index];
        var o = this.current;
        var x = slide.offsetLeft - o.padding;
        var last = this.slides[this.slides.length - 1];
        var maxX = last.offsetLeft + last.offsetWidth - (this.track.clientWidth - o.padding);
        return Math.max(0, Math.min(x, Math.max(0, maxX)));
      }

      go(index, instant) {
        if (!this.mounted) return;
        var prev = this.index;
        this.index = this.clamp(index);
        this.translate(-this.positionOf(this.index), instant);
        this.update();
        if (this.pauseVideos && prev !== this.index) this.handleHiddenVideos();
      }

      translate(x, instant) {
        this.list.style.transition = instant ? 'none' : 'transform ' + SPEED + 'ms ' + EASING;
        this.list.style.transform = 'translateX(' + x + 'px)';
      }

      update() {
        var self = this;
        var o = this.current;
        var active = this.slides[this.index];
        this.slides.forEach(function (slide, i) {
          var visible = i >= self.index && i < self.index + o.perPage;
          slide.classList.toggle('is-active', i === self.index);
          slide.classList.toggle('is-visible', visible);
          slide.classList.toggle('is-prev', i === self.index - 1);
          slide.classList.toggle('is-next', i === self.index + 1);
          slide.classList.toggle('is-instant-active', i === self.index);
          if (visible) slide.removeAttribute('aria-hidden');
          else slide.setAttribute('aria-hidden', 'true');
        });
        if (active) this.style.setProperty('--active-slide-height', active.offsetHeight + 'px');
        if (this.prevBtn) this.prevBtn.disabled = !this.rewind && this.index <= 0;
        if (this.nextBtn) this.nextBtn.disabled = !this.rewind && this.index >= this.end;
        if (this.pageButtons) {
          var page = this.indexToPage(this.index);
          this.pageButtons.forEach(function (btn, i) {
            btn.classList.toggle('is-active', i === page);
            btn.setAttribute('aria-selected', i === page ? 'true' : 'false');
          });
        }
      }

      handleHiddenVideos() {
        this.slides.forEach(function (slide) {
          if (slide.classList.contains('is-visible')) return;
          var video = slide.querySelector('internal-video');
          if (!video || !video.video) return;
          var action = video.dataset.actionOnInactive;
          if (action === 'pause') {
            video.video.pause();
            video.classList.remove('internal-video--playing');
          } else if (action === 'mute') {
            video.video.muted = true;
            video.classList.add('internal-video--muted');
          }
        });
      }

      onResize() {
        if (!this.mounted) return;
        var self = this;
        clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(function () {
          self.layout();
          self.go(self.index, true);
        }, 100);
      }

      onKeydown(event) {
        if (event.key === 'ArrowLeft') { this.go(this.index - this.current.perMove); event.preventDefault(); }
        if (event.key === 'ArrowRight') { this.go(this.index + this.current.perMove); event.preventDefault(); }
      }

      /* ---- drag / swipe */
      onPointerDown(event) {
        if (!this.overflow || (event.pointerType === 'mouse' && event.button !== 0)) return;
        this.drag = {
          startX: event.clientX,
          startY: event.clientY,
          base: -this.positionOf(this.index),
          moved: false,
          startTime: Date.now()
        };
        this.list.style.transition = 'none';
        window.addEventListener('pointermove', this.onPointerMove);
        window.addEventListener('pointerup', this.onPointerUp);
        window.addEventListener('pointercancel', this.onPointerUp);
      }

      onPointerMove(event) {
        if (!this.drag) return;
        var dx = event.clientX - this.drag.startX;
        var dy = event.clientY - this.drag.startY;
        if (!this.drag.moved) {
          if (Math.abs(dx) < 4 && Math.abs(dy) < 4) return;
          if (Math.abs(dy) > Math.abs(dx)) { this.endDrag(); return; }
          this.drag.moved = true;
          this.root.classList.add('is-dragging');
        }
        event.preventDefault();
        this.list.style.transform = 'translateX(' + (this.drag.base + dx) + 'px)';
      }

      onPointerUp(event) {
        if (!this.drag) return;
        var dx = event.clientX - this.drag.startX;
        var moved = this.drag.moved;
        var dt = Math.max(1, Date.now() - this.drag.startTime);
        this.endDrag();
        if (!moved) return;
        this.suppressClick = true;
        var self = this;
        setTimeout(function () { self.suppressClick = false; }, 50);
        var slideWidth = this.slides[0].offsetWidth + this.current.gap;
        var velocity = Math.abs(dx) / dt;
        var steps = Math.round(Math.abs(dx) / slideWidth);
        if (steps === 0 && (Math.abs(dx) > slideWidth * 0.2 || velocity > 0.5)) steps = 1;
        steps = Math.max(steps, 0);
        if (steps === 0) { this.go(this.index, false); return; }
        this.go(this.index + (dx < 0 ? steps : -steps) * this.current.perMove);
      }

      endDrag() {
        this.drag = null;
        this.root.classList.remove('is-dragging');
        window.removeEventListener('pointermove', this.onPointerMove);
        window.removeEventListener('pointerup', this.onPointerUp);
        window.removeEventListener('pointercancel', this.onPointerUp);
      }

      onClickCapture(event) {
        if (this.suppressClick) {
          event.preventDefault();
          event.stopPropagation();
        }
      }
    }
    customElements.define('splide-component', SplideComponent);
  }
})();
